import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './Sidebar.module.css';
import { Module, NavigationItem } from '../../../types';
import {
  DashboardIcon,
  GebiedsanalyseIcon,
  SentimentIcon,
  PersonasIcon,
  StrategyIcon,
  MediaIcon,
  WerkplanningIcon,
  VragenIcon,
  RelatiebeheerIcon,
  ProjectWebsiteIcon
} from '../../common/Icons/Icons';
import { sentimentAPI, questionsAPI, relationshipsAPI, tasksAPI } from '../../../services/api';

interface SidebarProps {
  activeModule: Module;
  onModuleChange: (module: Module) => void;
  enabledModules?: Record<string, boolean>;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeModule, onModuleChange, enabledModules }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [counts, setCounts] = useState<Record<string, number>>({
    sentiment: 0,
    vragen: 0,
    relatiebeheer: 0,
    werkplanning: 0
  });

  useEffect(() => {
    if (projectId) {
      fetchCounts();
      // Refresh counts every 30 seconds
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [projectId]);

  const fetchCounts = async () => {
    if (!projectId) return;

    try {
      // Fetch counts in parallel
      const [sentimentData, questionsData, relationshipsData, tasksData] = await Promise.all([
        sentimentAPI.getByProject(projectId).catch(() => ({ sentiments: [], count: 0 })),
        questionsAPI.getByProject(projectId).catch(() => ({ questions: [], count: 0 })),
        relationshipsAPI.getByProject(projectId).catch(() => ({ relationships: [], count: 0 })),
        tasksAPI.getByProject(projectId).catch(() => [])
      ]);

      // Count unreplied sentiment items
      const unrepliedSentiments = sentimentData.sentiments?.filter((s: any) =>
        s.status === 'new' || s.status === 'pending'
      ).length || 0;

      // Count open questions
      const openQuestions = questionsData.questions?.filter((q: any) =>
        q.status === 'open' || q.status === 'pending' || !q.answer
      ).length || questionsData.count || 0;

      // Count active relationships
      const activeRelationships = relationshipsData.relationships?.length ||
        relationshipsData.count || 0;

      // Count pending tasks
      const pendingTasks = Array.isArray(tasksData) ?
        tasksData.filter((t: any) => t.status !== 'completed').length : 0;

      setCounts({
        sentiment: unrepliedSentiments,
        vragen: openQuestions,
        relatiebeheer: activeRelationships,
        werkplanning: pendingTasks
      });
    } catch (error) {
      console.error('Failed to fetch sidebar counts:', error);
    }
  };

  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      id: 'gebiedsanalyse',
      label: 'Gebiedsanalyse',
      icon: <GebiedsanalyseIcon />,
    },
    {
      id: 'sentiment',
      label: 'Sentiment',
      icon: <SentimentIcon />,
      badge: counts.sentiment > 0 ? counts.sentiment : undefined,
    },
    {
      id: 'personas',
      label: "Persona's",
      icon: <PersonasIcon />,
    },
    {
      id: 'strategy',
      label: 'Strategie',
      icon: <StrategyIcon />,
    },
    {
      id: 'media',
      label: 'Middelen',
      icon: <MediaIcon />,
    },
    {
      id: 'werkplanning',
      label: 'Werkplanning',
      icon: <WerkplanningIcon />,
      badge: counts.werkplanning > 0 ? counts.werkplanning : undefined,
    },
    {
      id: 'vragen',
      label: 'Vragen',
      icon: <VragenIcon />,
      badge: counts.vragen > 0 ? counts.vragen : undefined,
    },
    {
      id: 'relatiebeheer',
      label: 'Relatiebeheer',
      icon: <RelatiebeheerIcon />,
      badge: counts.relatiebeheer > 0 ? counts.relatiebeheer : undefined,
    },
    {
      id: 'projectwebsite',
      label: 'Projectwebsite',
      icon: <ProjectWebsiteIcon />,
    },
    {
      id: 'issue-radar',
      label: 'Issue Radar',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>,
    },
    {
      id: 'participatie',
      label: 'Participatie',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    },
    {
      id: 'rapportage',
      label: 'Rapportage',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    },
    {
      id: 'stakeholder-netwerk',
      label: 'Netwerk',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="3"/><circle cx="5" cy="19" r="3"/><circle cx="19" cy="19" r="3"/><line x1="12" y1="8" x2="5" y2="16"/><line x1="12" y1="8" x2="19" y2="16"/><line x1="5" y1="19" x2="19" y2="19"/></svg>,
    },
    {
      id: 'vergunningen',
      label: 'Vergunningen',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M9 15l2 2 4-4"/></svg>,
    },
    {
      id: 'kennisbank',
      label: 'Kennisbank',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
    },
  ];

  // Modules die standaard uit staan en expliciet ingeschakeld moeten worden
  const optInModules = new Set(['participatie', 'rapportage', 'stakeholder-netwerk', 'vergunningen', 'kennisbank', 'issue-radar']);

  const visibleItems = enabledModules
    ? navigationItems.filter(item =>
        optInModules.has(item.id)
          ? enabledModules[item.id] === true
          : enabledModules[item.id] !== false
      )
    : navigationItems;

  return (
    <nav className={styles.sidebar}>
      {visibleItems.map((item) => (
        <button
          key={item.id}
          className={`${styles.navItem} ${activeModule === item.id ? styles.active : ''}`}
          onClick={() => onModuleChange(item.id as Module)}
          title={item.badge ? `${item.label} (${item.badge})` : item.label}
        >
          <span className={styles.navIcon}>{item.icon}</span>
          <span>{item.label}</span>
          {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
        </button>
      ))}
    </nav>
  );
};